/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useCrmContacts.ts
 * PURPOSE: List contacts from dashboard shell CRM API — delayed DELETE commit (undo window) like deals
 * DEPENDENCIES: react, @shared/lib/frontend/api/crmApi, ./crmEntityMappers, @shared/lib/frontend/api/contactApi ManagedContact
 * PUBLIC_API: useCrmContacts, ContactDeleteCommitHandlers
 * SIDE_EFFECTS: HTTP
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-05-08 **`cancelContactDelete`** clears timer + restores row; schedule **`cancel()`** delegates to it — Residual 2 UX parity with deals
 * RECENT_PRIOR: 2026-05-08 [2.2.879] **`scheduleContactDeleteCommit`**, optimistic **`removeContact`** / **`restoreContact`** — shell **`CHANGELOG` [2.2.879]`**
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { crmDelete, crmList } from '@shared/lib/frontend/api/crmApi';
import type { ManagedContact } from '@shared/lib/frontend/api/contactApi';
import { mapCrmShellContactToManaged } from './crmEntityMappers';

const CONTACT_DELETE_UNDO_MS = 5000;

export type ContactDeleteCommitHandlers = {
  onCommitted: () => void;
  onCommitFailed: (err: unknown) => void;
};

export type UseCrmContactsMutate = {
  deleteContact: (id: string) => Promise<void>;
  /** Remove from in-memory list and stash a snapshot for `restoreContact` / failed delayed delete. */
  removeContact: (id: string) => void;
  /** Re-insert stashed contact (rollback or if you cleared the timer without `cancelContactDelete`). */
  restoreContact: (id: string) => void;
  /** Clears pending DELETE timer and restores the row from snapshot (Undo). */
  cancelContactDelete: (id: string) => void;
  /**
   * After the delay, DELETEs the contact on the server and refetches. The returned `cancel()` calls
   * `cancelContactDelete` (same as Undo).
   * Caller should call `removeContact` before scheduling so the snapshot exists.
   */
  scheduleContactDeleteCommit: (id: string, handlers: ContactDeleteCommitHandlers) => () => void;
};

export type UseCrmContactsResult = {
  data: ManagedContact[];
  isLoading: boolean;
  error: string | null;
  mutate: UseCrmContactsMutate;
  refetch: () => Promise<void>;
};

function sortContactsStable(rows: ManagedContact[]): ManagedContact[] {
  return [...rows].sort((a, b) => {
    const an = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
    if (an !== 0) return an;
    return a.id.localeCompare(b.id);
  });
}

export function useCrmContacts(): UseCrmContactsResult {
  const [data, setData] = useState<ManagedContact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pendingContactSnapshots = useRef(new Map<string, ManagedContact>());
  const pendingContactDeleteTimers = useRef(new Map<string, number>());

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await crmList<Record<string, unknown>>('contacts', { limit: 200 });
      const rows = Array.isArray(res.items) ? res.items : [];
      setData(rows.map((r) => mapCrmShellContactToManaged(r)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const deleteContact = useCallback(async (id: string) => {
    await crmDelete('contacts', id);
    await refetch();
  }, [refetch]);

  const removeContact = useCallback((id: string) => {
    setData((prev) => {
      const row = prev.find((c) => c.id === id);
      if (!row) return prev;
      if (!pendingContactSnapshots.current.has(id)) {
        pendingContactSnapshots.current.set(id, { ...row });
      }
      return prev.filter((c) => c.id !== id);
    });
  }, []);

  const restoreContact = useCallback((id: string) => {
    const snap = pendingContactSnapshots.current.get(id);
    if (!snap) return;
    pendingContactSnapshots.current.delete(id);
    setData((prev) => {
      if (prev.some((c) => c.id === id)) return prev;
      return sortContactsStable([...prev, snap]);
    });
  }, []);

  const cancelContactDelete = useCallback((id: string) => {
    const t = pendingContactDeleteTimers.current.get(id);
    if (t != null) {
      window.clearTimeout(t);
      pendingContactDeleteTimers.current.delete(id);
    }
    restoreContact(id);
  }, [restoreContact]);

  const scheduleContactDeleteCommit = useCallback(
    (id: string, handlers: ContactDeleteCommitHandlers) => {
      const prevTid = pendingContactDeleteTimers.current.get(id);
      if (prevTid != null) window.clearTimeout(prevTid);
      const tid = window.setTimeout(() => {
        pendingContactDeleteTimers.current.delete(id);
        void (async () => {
          try {
            await crmDelete('contacts', id);
            await refetch();
            pendingContactSnapshots.current.delete(id);
            handlers.onCommitted();
          } catch (e) {
            const snap = pendingContactSnapshots.current.get(id);
            if (snap) {
              setData((rows) => (rows.some((c) => c.id === id) ? rows : sortContactsStable([...rows, snap])));
              pendingContactSnapshots.current.delete(id);
            }
            handlers.onCommitFailed(e);
          }
        })();
      }, CONTACT_DELETE_UNDO_MS);
      pendingContactDeleteTimers.current.set(id, tid);
      return () => {
        cancelContactDelete(id);
      };
    },
    [refetch, cancelContactDelete]
  );

  const mutate = useMemo(
    () =>
      ({
        deleteContact,
        removeContact,
        restoreContact,
        cancelContactDelete,
        scheduleContactDeleteCommit,
      }) satisfies UseCrmContactsMutate,
    [deleteContact, removeContact, restoreContact, cancelContactDelete, scheduleContactDeleteCommit]
  );

  return { data, isLoading, error, mutate, refetch };
}
